import { mergeAttributes, Node } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type {
  FrontMatterFieldAttrs,
  FrontMatterScalarType,
  FrontMatterValueKind,
} from "./frontMatter.js";
import { FRONT_MATTER_FIELD_NODE_NAME, FRONT_MATTER_NODE_NAME } from "./frontMatter.js";

/**
 * HTML 속성값에서 개행은 파서가 정규화하고, 엔티티로 직렬화되는 문자는 정적 HTML
 * 파서가 되돌려주지 않아 원문이 손상된다. 마크업 의미를 가지는 문자와 개행을
 * `\uXXXX`로 인코딩해 CRLF까지 무손실로 왕복시킨다. 역슬래시 자체도 인코딩하므로
 * 속성값에 남은 `\uXXXX`는 항상 인코더가 만든 것이어서 복원이 모호하지 않다.
 */
const ENCODED_SOURCE_CHARS = /[\\&<>"'\r\n]/g;
const ENCODED_SOURCE_SEQUENCE = /\\u([0-9a-f]{4})/g;

function encodeSource(value: string): string {
  return value.replace(
    ENCODED_SOURCE_CHARS,
    (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`
  );
}

function decodeSource(value: string): string {
  return value.replace(ENCODED_SOURCE_SEQUENCE, (_match, code: string) =>
    String.fromCharCode(Number.parseInt(code, 16))
  );
}

/**
 * 직렬화 전용 원문 attrs까지 정적 HTML에 남겨야 백엔드 `generateHTML`/`generateJSON`
 * 왕복에서 데이터가 사라지지 않는다. 값은 모두 `data-*` 속성으로만 렌더하고
 * 마크업 이스케이프는 Tiptap 렌더러에 맡긴다.
 */
function dataAttribute(name: string, dataName: string) {
  return {
    default: "",
    parseHTML: (element: HTMLElement) => decodeSource(element.getAttribute(dataName) ?? ""),
    renderHTML: (attributes: Record<string, unknown>) => ({
      [dataName]: encodeSource(String(attributes[name] ?? "")),
    }),
  };
}

const SCALAR_TYPES: FrontMatterScalarType[] = ["string", "number", "date", "boolean", "null"];
const VALUE_KINDS: FrontMatterValueKind[] = ["scalar", "array", "complex"];

function parseScalarType(value: string | null): FrontMatterScalarType | null {
  return SCALAR_TYPES.find((scalarType) => scalarType === value) ?? null;
}

function parseValueKind(value: string | null): FrontMatterValueKind {
  return VALUE_KINDS.find((valueKind) => valueKind === value) ?? "scalar";
}

/**
 * 카드는 최상위 블록으로만 존재하므로 top-level offset만 본다.
 * "선두 한 장"이라는 배치 규칙을 쓰는 곳이 모두 같은 목록을 보게 하려고 공유 패키지에 둔다.
 */
export function frontMatterPositions(doc: ProseMirrorNode): number[] {
  const positions: number[] = [];
  doc.forEach((node, offset) => {
    if (node.type.name === FRONT_MATTER_NODE_NAME) positions.push(offset);
  });
  return positions;
}

export const FrontMatter = Node.create({
  name: FRONT_MATTER_NODE_NAME,
  group: "block",
  content: `${FRONT_MATTER_FIELD_NODE_NAME}*`,
  isolating: true,
  selectable: true,
  defining: true,

  addAttributes() {
    return {
      trailingSource: dataAttribute("trailingSource", "data-trailing-source"),
    };
  },

  parseHTML() {
    return [{ tag: `section[data-type="${FRONT_MATTER_NODE_NAME}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["section", mergeAttributes(HTMLAttributes, { "data-type": FRONT_MATTER_NODE_NAME }), 0];
  },
});

export const FrontMatterField = Node.create({
  name: FRONT_MATTER_FIELD_NODE_NAME,
  content: "text*",
  isolating: true,
  defining: true,
  // complex 값은 콜론 뒤 원문(선행 개행과 들여쓰기 포함)을 그대로 담으므로 정적 HTML을
  // 다시 파싱할 때 공백이 접히면 안 된다.
  whitespace: "pre",

  addAttributes() {
    return {
      key: dataAttribute("key", "data-key"),
      valueKind: {
        default: "scalar" satisfies FrontMatterFieldAttrs["valueKind"],
        parseHTML: (element: HTMLElement) =>
          parseValueKind(element.getAttribute("data-value-kind")),
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-value-kind": String(attributes.valueKind ?? "scalar"),
        }),
      },
      scalarType: {
        // array/complex 필드의 `null`을 그대로 복원하기 위해 기본값도 `null`로 둔다.
        // Tiptap은 `parseHTML`이 `null`을 돌려주면 기본값을 사용한다.
        default: null as FrontMatterScalarType | null,
        parseHTML: (element: HTMLElement) =>
          parseScalarType(element.getAttribute("data-scalar-type")),
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-scalar-type": attributes.scalarType === null ? "" : String(attributes.scalarType),
        }),
      },
      originalKey: dataAttribute("originalKey", "data-original-key"),
      originalCanonicalValue: dataAttribute(
        "originalCanonicalValue",
        "data-original-canonical-value"
      ),
      originalSource: dataAttribute("originalSource", "data-original-source"),
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${FRONT_MATTER_FIELD_NODE_NAME}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": FRONT_MATTER_FIELD_NODE_NAME }),
      0,
    ];
  },
});
